import { useState } from 'react';
import { Alert, Badge, Button, Group, Switch, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconCalendarStats, IconClockEdit, IconGift } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../SectionCard';
import { formatSom, tzDateKey } from '../../lib/format';
import { KetishModal, soatTZ, type OchiqYozuv } from './KetishModal';

/** Backend `lib/kunlarJadvali.KunQatori` ning JSON shakli. */
export interface KunQatoriJson {
  kun: string;
  ishKuni: boolean;
  holat: 'KELDI' | 'ISHDA' | 'KELMADI' | 'KUTILMOQDA' | 'DAM' | 'TASHQARI';
  keldi: string | null;
  ketdi: string | null;
  ketishYoq: boolean;
  ishladiDaqiqa: number;
  smena: string | null;
  kechikish: number | null;
  ertaKetish: number | null;
  ortiqcha: number | null;
  soat: number | null;
  summa: number | null;
  hozirgacha: boolean;
  tuzatish: number;
  yozuvlar: { id: string; checkIn: string; checkOut: string | null }[];
}

const RANG: Record<KunQatoriJson['holat'], string> = {
  KELDI: 'teal', ISHDA: 'blue', KELMADI: 'red', KUTILMOQDA: 'gray', DAM: 'gray', TASHQARI: 'orange',
};

/**
 * KUNLAR BO'YICHA — oyning har kuni: holat, keldi, ketdi, ishlagan vaqt va shu kun puli.
 * Ketish belgilanmagan kunda "Ketgan vaqtini kiritish" (KetishModal). Ish vaqti (smena)
 * bo'lsa kechikish / erta ketish va ortiqcha uchun "Bonus" ham shu yerda.
 * Kun puli serverdan (`kunlar[].summa`) — bu yerda hisoblanmaydi.
 */
export function KunlarJadvali({ kunlar, oyNomi, kunbay, onBonus }: {
  kunlar: KunQatoriJson[];
  oyNomi: string;
  /** Maosh turi kun bo'yicha hisoblanadimi (oylik qoida oyida / soatbay). Yo'q bo'lsa pul ustuni yashirin. */
  kunbay: boolean;
  onBonus?: (kun: string, ortiqcha: number) => void;
}) {
  const { t } = useTranslation();
  const [damKorsat, setDamKorsat] = useState(false);
  const [ochiq, setOchiq] = useState<{ yozuv: OchiqYozuv; sukut: string | null } | null>(null);
  const bugun = tzDateKey(new Date());
  const hm = (min: number) => (min % 60 ? t('kunlar.h', { h: Math.floor(min / 60), m: min % 60 }) : `${min / 60} ${t('staffDetail.h')}`);
  const smenali = kunlar.some((h) => h.smena);
  const hafta = (kun: string) => {
    const d = new Date(`${kun}T12:00:00+05:00`).getUTCDay();
    return t(`days.${d === 0 ? 7 : d}`);
  };

  // Dam olish kunlari (kelmagan) sukut bo'yicha yashirin — jadval faqat ish kunlari, kelgan kunlar
  // va bonus/jarima yozilgan kunlar (shanba bonusi ko'rinmay qolmasin)
  const qatorlar = kunlar.filter((h) => damKorsat || h.holat !== 'DAM' || h.tuzatish !== 0);
  const kelgan = kunlar.filter((h) => h.holat === 'KELDI' || h.holat === 'ISHDA').length;
  const jamiDaqiqa = kunlar.reduce((n, h) => n + h.ishladiDaqiqa, 0);
  const jamiPul = kunlar.reduce((n, h) => n + (h.summa ?? 0), 0);
  const jamiTuz = kunlar.reduce((n, h) => n + h.tuzatish, 0);
  const ketishsiz = kunlar.filter((h) => h.ketishYoq && h.kun < bugun).length;

  const ketishniKirit = (h: KunQatoriJson) => {
    const y = [...h.yozuvlar].reverse().find((l) => !l.checkOut);
    if (!y) return;
    // Sukut — smena oxiri (bo'lsa): "08:00–14:00, 15:00–17:00" → "17:00"
    const oxiri = h.smena ? h.smena.split(', ').map((o) => o.split('–')[1]).sort().pop() ?? null : null;
    setOchiq({ yozuv: { id: y.id, checkIn: y.checkIn }, sukut: h.kun === bugun ? null : oxiri });
  };

  return (
    <SectionCard
      title={t('kunlar.title', { month: oyNomi })}
      subtitle={t('kunlar.subtitle')}
      icon={IconCalendarStats}
      p={0}
      right={<Switch size="xs" label={t('kunlar.showRest')} checked={damKorsat} onChange={(e) => setDamKorsat(e.currentTarget.checked)} />}
    >
      {ketishsiz > 0 && (
        <Alert mx="md" mb="sm" color="orange" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text fz="sm">{t('kunlar.noOutHint', { n: ketishsiz })}</Text>
        </Alert>
      )}
      {!kunbay && <Text px="md" pb="xs" size="xs" c="dimmed">{t('kunlar.noPerDay')}</Text>}
      <Table.ScrollContainer minWidth={smenali ? 900 : 760}>
        <Table verticalSpacing="xs" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('kunlar.date')}</Table.Th>
              <Table.Th>{t('kunlar.status')}</Table.Th>
              {smenali && <Table.Th>{t('kunlar.shift')}</Table.Th>}
              <Table.Th>{t('kunlar.in')}</Table.Th>
              <Table.Th>{t('kunlar.out')}</Table.Th>
              <Table.Th>{t('kunlar.workedCol')}</Table.Th>
              {kunbay && <Table.Th ta="right">{t('kunlar.dayPay')}</Table.Th>}
              <Table.Th ta="right">{t('kunlar.adj')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {qatorlar.map((h) => {
              const xira = h.holat === 'DAM' || h.holat === 'KUTILMOQDA' || h.holat === 'TASHQARI';
              return (
                <Table.Tr key={h.kun} style={xira ? { opacity: 0.6 } : undefined}>
                  <Table.Td>
                    <Text size="sm" fw={600} ff="monospace">{h.kun.slice(8, 10)}.{h.kun.slice(5, 7)}</Text>
                    <Text size="xs" c="dimmed">{hafta(h.kun)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={RANG[h.holat]} variant="light">{t(`kunlar.s_${h.holat}`)}</Badge>
                    {!h.ishKuni && (h.holat === 'KELDI' || h.holat === 'ISHDA') && (
                      <Text size="xs" c="dimmed">{t('kunlar.s_DAM')}</Text>
                    )}
                  </Table.Td>
                  {smenali && <Table.Td><Text size="sm" c="dimmed">{h.smena ?? '—'}</Text></Table.Td>}
                  <Table.Td>
                    {h.keldi ? <Text size="sm" fw={600}>{soatTZ(h.keldi)}</Text> : <Text size="sm" c="dimmed">—</Text>}
                    {!!h.kechikish && <Text size="xs" c="red.7">{t('kunlar.late', { n: h.kechikish })}</Text>}
                  </Table.Td>
                  <Table.Td>
                    {h.ketishYoq ? (
                      <Group gap={6} wrap="nowrap">
                        {h.ketdi && <Text size="sm">{soatTZ(h.ketdi)}</Text>}
                        <Button
                          size="compact-xs" variant="light" color={h.holat === 'ISHDA' ? 'blue' : 'orange'}
                          leftSection={<IconClockEdit size={13} />} onClick={() => ketishniKirit(h)}
                        >
                          {t('kunlar.setOut')}
                        </Button>
                      </Group>
                    ) : h.ketdi ? <Text size="sm" fw={600}>{soatTZ(h.ketdi)}</Text> : <Text size="sm" c="dimmed">—</Text>}
                    {!!h.ertaKetish && !h.hozirgacha && <Text size="xs" c="red.7">{t('kunlar.early', { n: h.ertaKetish })}</Text>}
                  </Table.Td>
                  <Table.Td>
                    {h.ishladiDaqiqa > 0
                      ? <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>{hm(h.ishladiDaqiqa)}</Text>
                      : <Text size="sm" c="dimmed">—</Text>}
                    {h.soat != null && <Text size="xs" c="dimmed">{h.soat} {t('staffDetail.h')}</Text>}
                  </Table.Td>
                  {kunbay && (
                    <Table.Td ta="right">
                      {h.summa == null ? (
                        <Text size="sm" c="dimmed">{h.ishKuni ? '—' : (h.holat === 'KELDI' ? t('kunlar.notCounted') : '—')}</Text>
                      ) : (
                        <>
                          <Text size="sm" fw={600} c={h.summa === 0 && h.holat === 'KELMADI' ? 'red.7' : undefined} style={{ whiteSpace: 'nowrap' }}>
                            {formatSom(h.summa)}
                          </Text>
                          {h.hozirgacha && <Text size="xs" c="dimmed">{t('kunlar.soFar')}</Text>}
                        </>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    <Group gap={6} justify="flex-end" wrap="nowrap">
                      {h.tuzatish !== 0 && (
                        <Text size="sm" fw={600} c={h.tuzatish > 0 ? 'teal.7' : 'red.7'} style={{ whiteSpace: 'nowrap' }}>
                          {h.tuzatish > 0 ? '+' : '−'}{formatSom(Math.abs(h.tuzatish))}
                        </Text>
                      )}
                      {onBonus && h.holat === 'KELDI' && (h.ortiqcha ?? 0) > 0 && (
                        <Button size="compact-xs" variant="light" color="teal" leftSection={<IconGift size={12} />} onClick={() => onBonus(h.kun, h.ortiqcha!)}>
                          +{h.ortiqcha} {t('ishVaqti.min')}
                        </Button>
                      )}
                      {h.tuzatish === 0 && !(onBonus && h.holat === 'KELDI' && (h.ortiqcha ?? 0) > 0) && <Text size="sm" c="dimmed">—</Text>}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th>{t('kunlar.total')}</Table.Th>
              <Table.Th>{t('kunlar.totalDays', { n: kelgan })}</Table.Th>
              {smenali && <Table.Th />}
              <Table.Th />
              <Table.Th />
              <Table.Th style={{ whiteSpace: 'nowrap' }}>{hm(jamiDaqiqa)}</Table.Th>
              {kunbay && <Table.Th ta="right" style={{ whiteSpace: 'nowrap' }}>{formatSom(jamiPul)}</Table.Th>}
              <Table.Th ta="right" style={{ whiteSpace: 'nowrap' }}>
                {jamiTuz === 0 ? '—' : `${jamiTuz > 0 ? '+' : '−'}${formatSom(Math.abs(jamiTuz))}`}
              </Table.Th>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>
      {kunbay && <Text px="md" py="xs" size="xs" c="dimmed">{t('kunlar.roundHint')}</Text>}
      <KetishModal yozuv={ochiq?.yozuv ?? null} sukut={ochiq?.sukut} onClose={() => setOchiq(null)} />
    </SectionCard>
  );
}
